// src/pages/dashboard/MyResearch.tsx
import React, { useEffect, useState } from 'react';
import {
  getInvestigations,
  deleteInvestigation,
} from '../../api/investigationsService';
import { getReports } from '../../api/reportsService';

import Loader from '../../components/Loader';
import PaidReportCard from '../../components/PaidReportCard';
import UnpaidReportCard from '../../components/UnpaidReportCard';
import InvestigationRequestCard from '../../components/MyResearch/InvestigationRequestCard';
import RequestInvestigationModal from '../../components/MyResearch/RequestInvestigationModal';
import Notification from '../../components/misc/Notification';
import { ReportDetailComponent } from '../../components/report_details/ReportDetailComponent';
import { useTranslation } from 'react-i18next';
import { ReportData, InvestigationData } from '../../types/interfaces';
import PurchaseModal from '../../components/PurchaseModal';
import NewInvestigationCard from '../../components/MyResearch/NewInvestigationCard';

import defaultDamelioLogo from '../../assets/damelio_logo_negro.png';
import unknownLogo from '../../assets/unknown_logo.png';
import useCurrency from '../../hooks/useCurrency';
import { symbolFor } from '../../utils/getSuggestedCurrency';

const MyResearch: React.FC = () => {
  const now = Date.now();
  const { currency, ready } = useCurrency();

  /* ────────────────────────────  ESTADOS  ─────────────────────────── */
  const [investigations, setInvestigations] = useState<InvestigationData[]>([]);
  const [reports, setReports]               = useState<ReportData[]>([]);
  const [loading, setLoading]               = useState(false);
  const [error, setError]                   = useState('');

  // búsqueda + filtros
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState<'all' | 'investigation' | 'unpaid' | 'paid'>(
    'all'
  );

  // modales / detalles
  const [selectedReportId,         setSelectedReportId]         = useState<string | null>(null);
  const [selectedReportForPurchase, setSelectedReportForPurchase] = useState<ReportData | null>(null);
  const [showRequestModal,         setShowRequestModal]         = useState(false);

  // notificación
  const [notificationMessage, setNotificationMessage] = useState('');
  const [showNotification,   setShowNotification]   = useState(false);

  const { t } = useTranslation();

  

  /* ────────────────────────────  CARGA INICIAL  ───────────────────── */
  useEffect(() => {
    if (!ready) return;
    loadData();
  }, [ready, currency]);

  const loadData = async () => {
    setLoading(true);
    setError('');
    try {
      const [invData, repData] = await Promise.all([getInvestigations(), getReports(currency)]);
      setInvestigations(invData);
      setReports(repData);
    } catch (err) {
      console.error('Error cargando datos:', err);
      setError('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  /* ────────────────────  UTILIDADES  ─────────────────── */
  const getFinalLogo = (url?: string | null) =>
    url === 'damelio'
      ? defaultDamelioLogo
      : !url || url.trim() === ''
      ? unknownLogo
      : url;

  /* ────────────────────  FILTROS / ORDEN  ─────────────────── */
  const unpaidReports   = reports.filter((r) => r.access_type === 'summary_view');
  const paidReports     = reports.filter((r) => r.access_type !== 'summary_view');
  const testPaidReports = paidReports.filter((r) => r.company_logo_url === 'damelio');
  const regularPaid     = paidReports.filter((r) => r.company_logo_url !== 'damelio');

  const sortedInvestigations: InvestigationData[] = [
    ...investigations.filter((i) => i.status === 'accepted'),
    ...investigations.filter((i) => i.status === 'under_review'),
    ...investigations.filter((i) => i.status === 'rejected'),
  ];

  let filteredByType: (InvestigationData | ReportData)[] = [];
  switch (filter) {
    case 'investigation':
      filteredByType = sortedInvestigations;
      break;
    case 'unpaid':
      filteredByType = unpaidReports;
      break;
    case 'paid':
      filteredByType = [...testPaidReports, ...regularPaid];
      break;
    default:
      filteredByType = [
        ...testPaidReports,
        ...sortedInvestigations,
        ...unpaidReports,
        ...regularPaid,
      ];
  }

  /* ───────────────  BUSCADOR (por company_name)  ─────────────── */
  const lowerSearch = searchTerm.trim().toLowerCase();
  const filteredItems = lowerSearch
    ? filteredByType.filter((item) =>
        item.company_name.toLowerCase().includes(lowerSearch)
      )
    : filteredByType;

  /* ─────────────────────────  RENDER CONDICIONAL  ────────────────── */
  if (!ready || loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader />
      </div>
    );
  }
  if (error) {
    return <p className="text-red-500 text-center">{error}</p>;
  }

  /*  Detalle de informe */
  if (selectedReportId) {
    const selected = reports.find((r) => r.id === selectedReportId);
    return (
      <div className="p-4">
        <button
          onClick={() => setSelectedReportId(null)}
          className="px-4 py-2 bg-gray-800 text-white rounded mb-4"
        >
          ← {t('myResearch.back')}
        </button>

        {selected ? (
          <ReportDetailComponent reportId={selectedReportId} summaryData={selected} />
        ) : (
          <p className="text-red-500">{t('myResearch.reportNotFound')} {selectedReportId}</p>
        )}
      </div>
    );
  }

  /* ─────────────────────────  VISTA PRINCIPAL  ───────────────────── */
  return (
    <div className="relative">
      <h1 className="text-5xl font-bold">{t('myResearch.title')}</h1>

      {/* ---------- BUSCADOR + FILTROS ---------- */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mt-6">
        <div className="flex flex-wrap items-center space-x-2">
          <input
            type="text"
            placeholder={t('myResearch.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="p-2 border-2 border-gray-400 rounded-lg focus:border-blue-600 placeholder-gray-500 w-full md:w-64 md:mr-4"
          />

          {/* filtros */}
          {([
            ['all',           t('myResearch.filterAll')],
            ['investigation', t('myResearch.filterInvestigations')],
            ['unpaid',        t('myResearch.filterUnpaid')],
            ['paid',          t('myResearch.filterPaid')],
          ] as const).map(([key, label]) => (
            <button
              key={key}
              className={`px-4 py-2 rounded ${
                filter === key ? 'bg-gray-800 text-white' : 'bg-gray-200 text-gray-800'
              }`}
              onClick={() => setFilter(key)}
            >
              {label}
            </button>
          ))}
        </div>

        {/* solicitar investigación */}
        <button
          className="mt-4 md:mt-0 px-6 py-3 bg-gray-800 text-white rounded-lg hover:bg-gray-900 transition-colors text-lg font-semibold"
          onClick={() => setShowRequestModal(true)}
        >
          {t('myResearch.requestInvestigation')}
        </button>
      </div>

      {/* ---------- LISTADO ---------- */}
      <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        <NewInvestigationCard onClick={() => setShowRequestModal(true)} />

        {filteredItems.map((item) => {
          /* INVESTIGACIÓN */
          if ('status' in item) {
            return (
              <InvestigationRequestCard
                key={item.id}
                investigation={item}
                onDelete={async (id) => {
                  await deleteInvestigation(id);
                  loadData();
                  setNotificationMessage(t('myResearch.investigationDeleted'));
                  setShowNotification(true);
                }}
              />
            );
          }

          /* REPORTES */
          const finalLogo = getFinalLogo(item.company_logo_url);
          const isTestReport = item.company_logo_url === 'damelio';

          if (item.access_type === 'summary_view') {
            const hasValidDiscount =
              item.discounted_price != null &&
              item.discount_percentage != null &&
              item.expires_at != null &&
              new Date(item.expires_at).getTime() > now;

            const discountInfo = hasValidDiscount
              ? {
                  originalPrice:      item.price,
                  discountedPrice:    item.discounted_price!,
                  discountPercentage: item.discount_percentage!,
                  discountEndDate:    item.expires_at!,
                }
              : undefined;

            return (
              <UnpaidReportCard
                key={item.id}
                id={Number(item.id)}
                company_name={item.company_name}
                company_logo_url={finalLogo}
                price={item.price}
                discountInfo={discountInfo}
                currencySymbol={symbolFor(currency)}
                onClick={() => setSelectedReportForPurchase(item)}
                summaryDownloadUrls={item.summary_download_urls ?? {}}
              />
            );
          }

          return (
            <PaidReportCard
              key={item.id}
              id={item.id}
              company_name={item.company_name}
              company_logo_url={finalLogo}
              payment_date={item.payment_date}
              onViewDetails={() => setSelectedReportId(item.id)}
              isTestReport={isTestReport}
            />
          );
        })}
      </div>

      {/* ---------- MODALES ---------- */}
      {showRequestModal && (
        <RequestInvestigationModal
          isOpen={showRequestModal}
          onClose={() => setShowRequestModal(false)}
          onSuccess={() => {
            loadData();
            setNotificationMessage(t('myResearch.investigationRequested'));
            setShowNotification(true);
          }}
        />
      )}

      {showNotification && (
        <Notification
          message={notificationMessage}
          onClose={() => setShowNotification(false)}
        />
      )}

      {selectedReportForPurchase && (
        <PurchaseModal
          reportId={selectedReportForPurchase.id}
          isOpen
          onClose={() => setSelectedReportForPurchase(null)}
          company={selectedReportForPurchase.company_name}
          logo={getFinalLogo(selectedReportForPurchase.company_logo_url)}
          price={selectedReportForPurchase.price}
          finalPrice={
            selectedReportForPurchase.discounted_price &&
            selectedReportForPurchase.discounted_price > 0
              ? selectedReportForPurchase.discounted_price
              : selectedReportForPurchase.price
          }
          discountPercentage={selectedReportForPurchase.discount_percentage ?? 0}
          discountEndDate={selectedReportForPurchase.expires_at || undefined}
          findings={{
            num_trabajadores:           selectedReportForPurchase.num_trabajadores,
            num_filtraciones:           selectedReportForPurchase.num_filtraciones,
            num_dominios_spoofeables:   selectedReportForPurchase.num_dominios_spoofeables,
            num_emails:                 selectedReportForPurchase.num_emails,
            num_ips:                    selectedReportForPurchase.num_ips,
            num_subdomains:             selectedReportForPurchase.num_subdomains,
            num_typosquatting:          selectedReportForPurchase.num_typosquatting,
            num_directories:            selectedReportForPurchase.num_directories,
            len_dir_sensibles:          selectedReportForPurchase.len_dir_sensibles,
            num_technologies:           selectedReportForPurchase.num_technologies,
            len_vulns:                  selectedReportForPurchase.len_vulns,
          }}
          currencySymbol={symbolFor(currency)}
          currency={currency}
          domains={selectedReportForPurchase.domains.map((d) => ({ domain_name: d }))}
          urls={selectedReportForPurchase.urls.map((u) => ({ url: u }))}
          
        />
      )}
    </div>
  );
};

export default MyResearch;
